import { useEffect } from 'react'
import { Skeleton } from 'antd'
import { useAppMessage } from '@providers'
import { useFollowUpFilialOrdemCompraQuery } from '@queries'
import { resolverMensagemErro } from '@utils'
import {
  CAMPOS_RESUMO_FILIAL,
  CAMPOS_RESUMO_ORDEM_COMPRA,
  TEXTOS_RESUMO_ORDEM_COMPRA,
} from '../../content'
import { formatarCep, formatarCnpj, formatarMoeda, formatarQuantidade } from '../../formatadores'
import type { FollowUpFilialResponse, FollowUpOrdemCompraResponse } from '../../../../types'
import './style.scss'

interface ResumoOrdemCompraProps {
  ordem: FollowUpOrdemCompraResponse
}

interface CampoResumo {
  rotulo: string
  valor: string
}

const SEM_VALOR = '—'

const montarCidade = (filial: FollowUpFilialResponse): string => {
  const cidade = filial.cidade?.trim()
  const uf = filial.uf?.trim()

  if (!cidade) return uf ?? SEM_VALOR

  return uf ? `${cidade} - ${uf}` : cidade
}

const montarCamposFilial = (filial: FollowUpFilialResponse): CampoResumo[] => [
  { rotulo: CAMPOS_RESUMO_FILIAL.razaoSocial, valor: filial.razaoSocial?.trim() || SEM_VALOR },
  { rotulo: CAMPOS_RESUMO_FILIAL.endereco, valor: filial.endereco?.trim() || SEM_VALOR },
  { rotulo: CAMPOS_RESUMO_FILIAL.cidade, valor: montarCidade(filial) },
  { rotulo: CAMPOS_RESUMO_FILIAL.cnpj, valor: filial.cnpj ? formatarCnpj(filial.cnpj) : SEM_VALOR },
  { rotulo: CAMPOS_RESUMO_FILIAL.cep, valor: filial.cep ? formatarCep(filial.cep) : SEM_VALOR },
]

export const ResumoOrdemCompra = ({ ordem }: ResumoOrdemCompraProps) => {
  const message = useAppMessage()

  const filialQuery = useFollowUpFilialOrdemCompraQuery({ idOrdemCompra: ordem.numero })

  useEffect(() => {
    if (filialQuery.error) {
      message.error(resolverMensagemErro(filialQuery.error, TEXTOS_RESUMO_ORDEM_COMPRA.erroConsultaFilial))
    }
  }, [filialQuery.error])

  const filial = filialQuery.data ?? null

  const camposOrdemCompra: CampoResumo[] = [
    { rotulo: CAMPOS_RESUMO_ORDEM_COMPRA.numero, valor: String(ordem.numero) },
    {
      rotulo: CAMPOS_RESUMO_ORDEM_COMPRA.quantidadeItens,
      valor: ordem.quantidadeItens === null ? SEM_VALOR : formatarQuantidade(ordem.quantidadeItens),
    },
    {
      rotulo: CAMPOS_RESUMO_ORDEM_COMPRA.valorTotal,
      valor: ordem.valorTotal === null ? SEM_VALOR : formatarMoeda(ordem.valorTotal),
    },
  ]

  const renderCampos = (campos: CampoResumo[]) => (
    <dl className={'resumo-follow-up-class__lista'}>
      {campos.map((campo) => (
        <div className={'resumo-follow-up-class__campo'} key={campo.rotulo}>
          <dt className={'resumo-follow-up-class__rotulo'}>{`${campo.rotulo}:`}</dt>
          <dd className={'resumo-follow-up-class__valor'}>{campo.valor}</dd>
        </div>
      ))}
    </dl>
  )

  return (
    <section className={'resumo-follow-up-class'} aria-label={TEXTOS_RESUMO_ORDEM_COMPRA.tituloOrdemCompra}>
      <article className={'resumo-follow-up-class__bloco'} aria-busy={filialQuery.isPending}>
        <h3 className={'resumo-follow-up-class__titulo'}>{TEXTOS_RESUMO_ORDEM_COMPRA.tituloFilial}</h3>
        <Skeleton loading={filialQuery.isPending} active paragraph={{ rows: 4 }} title={false}>
          {filial === null ? (
            <p className={'resumo-follow-up-class__indisponivel'}>{TEXTOS_RESUMO_ORDEM_COMPRA.filialIndisponivel}</p>
          ) : (
            renderCampos(montarCamposFilial(filial))
          )}
        </Skeleton>
      </article>

      <article className={'resumo-follow-up-class__bloco'}>
        <h3 className={'resumo-follow-up-class__titulo'}>{TEXTOS_RESUMO_ORDEM_COMPRA.tituloOrdemCompra}</h3>
        {renderCampos(camposOrdemCompra)}
      </article>
    </section>
  )
}
